import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Discount } from '@/schemas/discounts';
import { DiscountType } from '@/schemas/discounts';

interface DiscountFilters {
  type?: DiscountType;
  status?: 'active' | 'inactive' | 'expired' | 'usage_limit_reached' | 'scheduled';
  validity?: 'current' | 'upcoming' | 'expired';
  search?: string;
}

interface DiscountsState {
  byId: Record<string, Discount>;
  allIds: string[];
  filters: DiscountFilters;
  selectedDiscountId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: DiscountsState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedDiscountId: null,
  isLoading: false,
  error: null,
};

const discountsSlice = createSlice({
  name: 'discounts',
  initialState,
  reducers: {
    setDiscounts: (state, action: PayloadAction<Discount[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((discount) => {
        state.byId[discount.id] = discount;
        state.allIds.push(discount.id);
      });
    },
    addDiscount: (state, action: PayloadAction<Discount>) => {
      const discount = action.payload;
      state.byId[discount.id] = discount;
      if (!state.allIds.includes(discount.id)) {
        state.allIds.push(discount.id);
      }
    },
    updateDiscount: (state, action: PayloadAction<Discount>) => {
      const discount = action.payload;
      state.byId[discount.id] = discount;
    },
    removeDiscount: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    toggleDiscountActive: (state, action: PayloadAction<{ id: string; isActive: boolean }>) => {
      const discount = state.byId[action.payload.id];
      if (discount) {
        discount.is_active = action.payload.isActive;
      }
    },
    setFilters: (state, action: PayloadAction<Partial<DiscountFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setSelectedDiscount: (state, action: PayloadAction<string | null>) => {
      state.selectedDiscountId = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setDiscounts,
  addDiscount,
  updateDiscount,
  removeDiscount,
  toggleDiscountActive,
  setFilters,
  clearFilters,
  setSelectedDiscount,
  setLoading,
  setError,
} = discountsSlice.actions;

export default discountsSlice.reducer;
